<template>
  <div>
    <form class="space-y-4 text-center" @submit.prevent="handleUpdateProfile">
      <div class="flex items-center gap-4">
        <PrimeFloatLabel variant="on" class="flex-1">
          <label for="name">{{ $t('components.settings.profile.name') }}</label>
          <PrimeInputText id="name" v-model="profileInfo.name" type="text" />
        </PrimeFloatLabel>

        <PrimeFloatLabel variant="on" class="flex-1">
          <label for="surname">
            {{ $t('components.settings.profile.surname') }}
          </label>
          <PrimeInputText
            id="surname"
            v-model="profileInfo.surname"
            type="text"
          />
        </PrimeFloatLabel>
      </div>
      <PhoneInput
        class="flex-1 max-sm:w-full"
        @phone-number="handlePhoneNumber"
      />
      <div class="flex items-center gap-4">
        <PrimeFloatLabel variant="on" class="max-w-[48%] flex-1">
          <PrimeSelect
            v-model="profileInfo.location.country"
            input-id="countries"
            option-label="name"
            option-value="code"
            :options="countries"
            filter
            class="w-full text-start text-ellipsis"
          />
          <label for="countries">
            {{ $t('components.settings.profile.location') }}
          </label>
        </PrimeFloatLabel>
        <PrimeFloatLabel variant="on" class="flex-1">
          <label for="city">{{ $t('components.settings.profile.city') }}</label>
          <PrimeInputText
            id="city"
            v-model="profileInfo.location.city"
            type="text"
            class="w-full"
          />
        </PrimeFloatLabel>
      </div>
      <div class="flex w-full items-center gap-4">
        <PrimeFloatLabel variant="on" class="flex-1 max-sm:w-full">
          <PrimeDatePicker
            id="birthday"
            v-model="profileInfo.birthday"
            type="date"
            name="birthday"
            date-format="dd/mm/yy"
            :min-date="new Date('1900-01-01')"
            :max-date="new Date('2020-12-31')"
            class="w-full"
            placeholder="dd/mm/yyyy"
          />
          <label for="birthday">
            {{ $t('components.settings.profile.birthday') }}
          </label>
        </PrimeFloatLabel>
        <PrimeFloatLabel variant="on" class="flex-1">
          <PrimeSelect
            v-model="profileInfo.location.timezone"
            input-id="timezone"
            :options="timezones"
            class="w-full text-start"
          />
          <label for="timezone">
            {{ $t('components.settings.profile.timezone') }}
          </label>
        </PrimeFloatLabel>
      </div>

      <PrimeButton
        type="submit"
        :loading="pending"
        icon="pi pi-save"
        icon-pos="right"
        :label="
          pending
            ? $t('components.settings.profile.saving')
            : $t('components.settings.profile.save')
        "
        class="min-w-40"
      />
    </form>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import countries from '~/assets/jsons/countryCodes.json';
import timezones from '~/assets/jsons/timezones.json';
import PhoneInput from '../profile/PhoneInput.vue';

export default defineNuxtComponent({
  components: {
    PhoneInput,
  },
  data() {
    return {
      profileInfo: null,
      initialProfile: null,
      countries,
      timezones,
      pending: false,
    };
  },
  computed: {
    ...mapState(useAuthStore, ['profile']),
  },
  created() {
    this.initializeProfile();
  },
  methods: {
    ...mapActions(useAuthStore, ['updateProfile']),

    initializeProfile() {
      this.initialProfile = cloneDeep(this.profile);
      this.profileInfo = cloneDeep(this.profile);
    },

    handlePhoneNumber(number) {
      const isValid = number.length >= 10 && number.length <= 20;
      this.profileInfo.phone = isValid ? number : this.initialProfile.phone;
    },

    getChangedFields() {
      const changedFields = {};

      if (!isEqual(this.profileInfo.birthday, this.initialProfile.birthday)) {
        changedFields.birthday = this.profileInfo.birthday;
      }

      ['name', 'surname', 'phone'].forEach(field => {
        if (!isEqual(this.profileInfo[field], this.initialProfile[field])) {
          changedFields[field] = this.profileInfo[field];
        }
      });

      const locationChanged = !isEqual(
        this.profileInfo.location,
        this.initialProfile.location
      );

      if (locationChanged) {
        changedFields.location = {};

        ['country', 'city', 'timezone'].forEach(field => {
          if (
            !isEqual(
              this.profileInfo.location[field],
              this.initialProfile.location[field]
            )
          ) {
            changedFields.location[field] = this.profileInfo.location[field];
          }
        });

        if (Object.keys(changedFields.location).length === 0) {
          delete changedFields.location;
        }
      }

      return changedFields;
    },

    async handleUpdateProfile() {
      if (this.pending) return;

      const updatedFields = this.getChangedFields();
      if (Object.keys(updatedFields).length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: this.$t('components.settings.profile.noChanges'),
          detail: this.$t('components.settings.profile.noChangesDetected'),
          life: 3000,
        });
        return;
      }

      this.pending = true;

      try {
        const { error } = await this.updateProfile(updatedFields);

        if (error) {
          throw new Error(error);
        }

        this.$toast.add({
          severity: 'success',
          summary: this.$t('components.settings.profile.success'),
          detail: this.$t(
            'components.settings.profile.profileUpdatedSuccessfully'
          ),
          life: 4000,
        });

        // Başarı durumunda initialProfile'ı güncelle
        this.initialProfile = cloneDeep(this.profileInfo);
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('components.settings.profile.error'),
          detail:
            this.$t('errors.' + error) ||
            this.$t('components.settings.profile.updateFailed'),
          life: 4000,
        });
        this.profileInfo = cloneDeep(this.initialProfile);
      } finally {
        this.pending = false;
      }
    },
  },
});
</script>

<style>
.p-datepicker-input:not(:focus)::placeholder {
  color: transparent; /* Normalde placeholder görünmez */
  transition: color 0.3s ease;
}
</style>
